import type { UsageBreakdownRow } from '@shared/common-types.js'

/** 颜色和图例按什么分组：iOS 按大版本（27 / 26 / 18），App 的大版本都是 1，直接按 major.minor */
export type VersionGroupBy = 'major' | 'version'

export type VersionShare = {
  /** 「18.6」这种 major.minor */
  version: string
  /** 所属分组，「18」或「1.29」 */
  group: string
  people: number
  /** 占有效版本人数的比例 */
  share: number
  /** 比它低的版本加起来的比例，即「< 这个版本」 */
  below: number
}

export type VersionGroupShare = { group: string; people: number; share: number }

export type VersionShares = {
  /** 从低到高 */
  versions: VersionShare[]
  /** 分组合计，从低到高 */
  groups: VersionGroupShare[]
  /** 有效版本的总人数，比例的分母 */
  total: number
  /** 没带版本或解析不了的人数，不进分母 */
  unknown: number
}

function parseVersion(value: string): [number, number] | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value)
  if (!match) return null
  return [Number(match[1]), Number(match[2] ?? 0)]
}

/** 服务端已经按 major.minor 归好、一人一行；这里排序、算比例和「低于它」的累计比例 */
export function toVersionShares(rows: UsageBreakdownRow[], groupBy: VersionGroupBy): VersionShares {
  let unknown = 0
  const parsed: { version: string; major: number; minor: number; people: number }[] = []
  for (const row of rows) {
    const version = parseVersion(row.value)
    if (!version) {
      unknown += row.count
      continue
    }
    // 「18」和「18.0」是同一个版本，并到一起
    const [major, minor] = version
    const existing = parsed.find((item) => item.major === major && item.minor === minor)
    if (existing) existing.people += row.count
    else parsed.push({ version: `${major}.${minor}`, major, minor, people: row.count })
  }
  parsed.sort((a, b) => a.major - b.major || a.minor - b.minor)

  const total = parsed.reduce((sum, item) => sum + item.people, 0)
  let cumulative = 0
  const versions = parsed.map(({ version, major, people }) => {
    const below = total > 0 ? cumulative / total : 0
    cumulative += people
    const group = groupBy === 'major' ? String(major) : version
    return { version, group, people, share: total > 0 ? people / total : 0, below }
  })

  const groups: VersionGroupShare[] = []
  for (const item of versions) {
    const last = groups[groups.length - 1]
    if (last?.group === item.group) {
      last.people += item.people
      last.share += item.share
    } else groups.push({ group: item.group, people: item.people, share: item.share })
  }
  return { versions, groups, total, unknown }
}
